// src/App.js
import React, { useState, useEffect } from 'react';
import './index.css';
import { ReactLenis } from 'lenis/react';
import AmbientGradient from './components/AmbientGradient';
import RouteTransition from './components/RouteTransition';
import { AuthProvider, useAuth } from './context/AuthContext';
import { supabase } from './lib/supabase';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import CookieBanner from './components/CookieBanner';
import Auth from './pages/Auth';
import Landing from './pages/Landing';
import TrackRecord from './pages/TrackRecord';
import PublicDashboard from './pages/PublicDashboard';
import Contact from './pages/Contact';
import Legal from './pages/Legal';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Journal from './pages/Journal';
import NewTrade from './pages/NewTrade';
import Discipline from './pages/Discipline';
import RiskManager from './pages/RiskManager';
import SettingsPage from './pages/Settings';
import Markets from './pages/Markets';
import CalendarView from './pages/CalendarView';
import Calculator from './pages/Calculator';
import ImportExport from './pages/ImportExport';
import PerformanceLab from './pages/PerformanceLab';
import OnboardingModal from './components/OnboardingModal';
import PageTransition from './components/PageTransition';
import BetaGate, { isFounderUnlocked } from './pages/BetaGate';
import BrandHome from './pages/BrandHome';
import TradingResources from './pages/TradingResources';
import { useSupabaseTrades } from './hooks/useSupabaseTrades';
import { useAccounts } from './hooks/useAccounts';
import { useTrades } from './hooks/useTrades';

// ── Detect special URL routes ─────────────────────────────────────
// Returns one of:
//   'reset-password' | 'confirming' | 'track-record' | 'contact'
//   | 'legal-privacy' | 'legal-terms' | 'legal-disclaimer'
//   | 'public-<userId>'
//   | 'performance-lab'  ← gated platform area
//   | 'brand-home'       ← public ODFTrades brand homepage at "/"
//   | null               ← legacy fallback (treated as brand-home upstream)
function detectRoute() {
  const path   = window.location.pathname;
  const hash   = window.location.hash;
  const params = new URLSearchParams(window.location.search);
  if (params.get('type') === 'recovery' || hash.includes('type=recovery')) return 'reset-password';
  if (hash.includes('access_token') && hash.includes('type=signup'))       return 'confirming';
  if (path === '/track-record')      return 'track-record';
  if (path === '/contact')           return 'contact';
  if (path === '/privacy')           return 'legal-privacy';
  if (path === '/terms')             return 'legal-terms';
  if (path === '/disclaimer')        return 'legal-disclaimer';
  if (path === '/trading-resources') return 'trading-resources';
  const uMatch = path.match(/^\/u\/(.+)$/);
  if (uMatch) return `public-${uMatch[1]}`;
  if (path === '/performance-lab' || path === '/lab') return 'performance-lab';
  if (path === '/' || path === '')                    return 'brand-home';
  // Unknown path — show brand home as the safe default
  return 'brand-home';
}

// Module-level flag: set to true the first time the user navigates
// inside the SPA. We use it so the "Back" buttons can use the browser
// back arrow when there IS in-app history, and fall back to the brand
// home when the user landed directly on a deep link (history.back()
// would otherwise leave the site).
let inAppNavigated = false;

// Smart back — browser back if we have in-app history, else home.
function goBackSmart() {
  if (inAppNavigated) {
    window.history.back();
  } else {
    navigateTo('/');
  }
}

// Push URL state without a full page reload, then notify React.
function navigateTo(path) {
  if (window.location.pathname !== path) {
    inAppNavigated = true;
    window.history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

function AppInner() {
  const { user, loading } = useAuth();
  const [page,       setPage]       = useState('dashboard');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [legalTab, setLegalTab] = useState('privacy');
  const [view,     setView]     = useState('loading');
  const [lang,     setLang]     = useState(() => {
    try { return localStorage.getItem('odftrades_lang') || 'en'; } catch { return 'en'; }
  });

  const saveLang = l => {
    setLang(l);
    try { localStorage.setItem('odftrades_lang', l); } catch {}
  };

  const { settings, setSettings } = useTrades();
  const { accounts, activeAccountId, activeAccount, setActiveAccountId, createAccount, deleteAccount } = useAccounts();
  const { trades, addTrade, deleteTrade, updateTrade, refetch } = useSupabaseTrades(activeAccountId);

  // Check if new user needs onboarding
  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('onboarded').eq('id', user.id).single()
      .then(({ data }) => {
        if (data && !data.onboarded) setShowOnboarding(true);
      });
  }, [user]);

  // ── Navigation helpers ──────────────────────────────────────────
  const goHome        = () => setView('home'); // 'home' shows landing to logged-in users too
  const goAuth        = () => setView('auth');
  const goTrackRecord = () => setView('track-record');
  const goContact     = () => setView('contact');
  const goLegal       = tab => { setLegalTab(tab); setView('legal'); };

  // ── Logo home event from Sidebar ────────────────────────────────
  // Use ref to avoid stale closure on goHome
  const goHomeRef = React.useRef(null);
  goHomeRef.current = () => setView('home');
  useEffect(() => {
    const handler = () => goHomeRef.current();
    window.addEventListener('odftrades:gohome', handler);
    return () => window.removeEventListener('odftrades:gohome', handler);
  }, []);

  // ── Trade refresh event (after edit) ───────────────────────────
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener('odftrades:refreshtrades', handler);
    return () => window.removeEventListener('odftrades:refreshtrades', handler);
  }, [refetch]);

  // ── Determine initial view ──────────────────────────────────────
  useEffect(() => {
    if (loading) return;
    const route = detectRoute();
    if (route === 'reset-password')    { setView('reset-password'); return; }
    if (route === 'confirming')        { setView('app'); return; }
    if (route === 'track-record')      { setView('track-record'); return; }
    if (route === 'contact')           { setView('contact'); return; }
    if (route?.startsWith('legal'))    { setLegalTab(route.replace('legal-', '')); setView('legal'); return; }
    if (route?.startsWith('public-'))  { setView(route); return; }
    setView(user ? 'app' : 'landing');
  }, [loading, user]);

  // ── Auth state changes (cross-tab, email confirm) ───────────────
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const route = detectRoute();
        const isPublicView = route && (
          route === 'reset-password' || route === 'track-record' ||
          route === 'contact' || route?.startsWith('legal') || route?.startsWith('public-')
        );
        if (!isPublicView) {
          setView('app');
          if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
        }
      }
      if (event === 'SIGNED_OUT') {
        setView(v => v === 'reset-password' ? 'auth' : 'landing');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Build a stable key for page transitions ─────────────────────
  // Combining view + page means BOTH top-level routes (Landing → Auth)
  // and in-app navigation (Dashboard → Journal) trigger the overlay.
  const transitionKey =
    view === 'loading' || loading           ? 'loading'
    : view === 'reset-password'             ? 'reset'
    : view === 'track-record'               ? 'track-record'
    : view === 'contact'                    ? 'contact'
    : view === 'legal'                      ? `legal-${legalTab}`
    : view?.startsWith('public-')           ? view
    : !user && view === 'auth'              ? 'auth'
    : !user                                 ? 'landing-public'
    : view === 'home'                       ? 'landing-loggedin'
    :                                         `app-${page}`;

  // ── Compute the element to render ───────────────────────────────
  let routedContent;

  if (view === 'loading' || loading) {
    routedContent = (
      <div style={{ minHeight:'100vh', background:'#0a0a0f', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
        <div style={{ width:48, height:48, borderRadius:'50%', border:'2px solid #1e1e2e', borderTopColor:'#00e5a0', animation:'spin 0.8s linear infinite' }} />
        <div style={{ color:'#55556a', fontFamily:"'DM Mono',monospace", fontSize:11, letterSpacing:'0.1em' }}>LOADING</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  else if (view === 'reset-password') {
    routedContent = <ResetPassword onDone={async () => { try { await supabase.auth.signOut(); } catch(e){} setView('auth'); }} />;
  }
  else if (view === 'track-record')   routedContent = <TrackRecord onBack={goHome} onSignIn={goAuth} />;
  else if (view === 'contact')        routedContent = <Contact     onBack={goHome} onSignIn={goAuth} />;
  else if (view === 'legal')          routedContent = <Legal       page={legalTab} onBack={goHome} onSignIn={goAuth} />;
  else if (view?.startsWith('public-')) routedContent = <PublicDashboard userId={view.replace('public-', '')} onBack={goHome} onSignIn={goAuth} />;
  else if (!user) {
    routedContent = view === 'auth'
      ? <Auth onBack={goHome} />
      : (
        <>
          <Landing
            onEnter={goAuth} onSignIn={goAuth}
            onTrackRecord={goTrackRecord} onContact={goContact}
            onPrivacy={() => goLegal('privacy')}
            onTerms={() => goLegal('terms')}
            onDisclaimer={() => goLegal('disclaimer')}
            lang={lang} setLang={saveLang}
          />
          <CookieBanner />
        </>
      );
  }
  else if (view === 'home') {
    routedContent = (
      <>
        <Landing
          onEnter={() => setView('app')} onSignIn={() => setView('app')}
          onTrackRecord={goTrackRecord} onContact={goContact}
          onPrivacy={() => goLegal('privacy')}
          onTerms={() => goLegal('terms')}
          onDisclaimer={() => goLegal('disclaimer')}
          lang={lang} setLang={saveLang}
          loggedIn={true}
          onDashboard={() => setView('app')}
        />
        <CookieBanner />
      </>
    );
  }
  else {
    // ── Logged in (default) ─────────────────────────────────────
    const mergedSettings = {
      ...settings,
      startingBalance: activeAccount?.starting_balance || settings.startingBalance || 0,
    };
    const handleAddTrade = async trade => { await addTrade(trade); setPage('journal'); };
    const shareableLink  = `${window.location.origin}/u/${user.id}`;
    const renderPage = () => {
      switch (page) {
        case 'dashboard':     return <Dashboard    lang={lang} trades={trades} settings={mergedSettings} shareableLink={shareableLink} />;
        case 'journal':       return <Journal      lang={lang} trades={trades} onDelete={deleteTrade} onUpdate={updateTrade} settings={mergedSettings} />;
        case 'calendar':      return <CalendarView lang={lang} trades={trades} settings={mergedSettings} />;
        case 'new-trade':     return <NewTrade     lang={lang} onAdd={handleAddTrade} settings={mergedSettings} accounts={accounts} activeAccountId={activeAccountId} />;
        case 'discipline':    return <Discipline lang={lang}   trades={trades} />;
        case 'markets':       return <Markets lang={lang} />;
        case 'calculator':    return <Calculator lang={lang}   settings={mergedSettings} />;
        case 'risk':          return <RiskManager lang={lang}  trades={trades} settings={mergedSettings} />;
        case 'import-export': return <ImportExport lang={lang} trades={trades} settings={mergedSettings} onImport={addTrade} />
        case 'lab':           return <PerformanceLab lang={lang} trades={trades} settings={mergedSettings} onImport={addTrade} />;
        case 'settings':      return <SettingsPage lang={lang} trades={trades} settings={mergedSettings} setSettings={setSettings} onClear={() => {}} shareableLink={shareableLink} accounts={accounts} activeAccountId={activeAccountId} />;
        default:              return <Dashboard    trades={trades} settings={mergedSettings} shareableLink={shareableLink} />;
      }
    };
    routedContent = (
      <div style={{ display:'flex', minHeight:'100vh', background:'#0a0a0f' }} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
        <Sidebar active={page} onChange={setPage} lang={lang} />
        <div className="main-content" style={{ flex:1, display:'flex', flexDirection:'column', minHeight:'100vh' }}>
          <TopBar
            accounts={accounts} activeAccount={activeAccount}
            activeAccountId={activeAccountId} setActiveAccountId={setActiveAccountId}
            createAccount={createAccount} deleteAccount={deleteAccount}
            onHome={goHome} onTrackRecord={goTrackRecord}
            lang={lang} setLang={saveLang}
          />
          <main style={{ flex:1, background:'#0a0a0f', overflowY:'auto' }}>
            {renderPage()}
          </main>
        </div>
        <CookieBanner />
      </div>
    );
  }

  return (
    <PageTransition viewKey={transitionKey}>
      {routedContent}
    </PageTransition>
  );
}

// ── Brand-level router ────────────────────────────────────────────
// New architecture:
//   "/"               → BrandHome (public, no beta gate)
//   "/contact"        → Contact   (public)
//   "/privacy"/etc    → Legal     (public)
//   "/track-record"   → TrackRecord (public)
//   "/u/<id>"         → PublicDashboard (public)
//   "/reset-password" → ResetPassword   (public)
//   "/performance-lab"→ Beta gate, then the existing platform (AppInner)
//
// The beta gate ONLY protects the Performance Lab area. The brand
// homepage and other marketing/public routes are fully open so
// visitors can read about ODFTrades without entering a code.
function BrandRouter() {
  const [route, setRoute] = useState(() => detectRoute());
  const [lang,  setLang]  = useState(() => {
    try { return localStorage.getItem('odftrades_lang') || 'en'; } catch { return 'en'; }
  });
  const saveLang = (l) => {
    setLang(l);
    try { localStorage.setItem('odftrades_lang', l); } catch {}
  };

  // Re-detect route on browser back/forward / programmatic navigation
  useEffect(() => {
    const onPop = () => { setRoute(detectRoute()); window.scrollTo(0, 0); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Helpers shared with the public pages — push URL, then re-detect
  const goBrand          = () => navigateTo('/');
  const goPerformanceLab = () => navigateTo('/performance-lab');
  const goContact        = () => navigateTo('/contact');
  const goPrivacy        = () => navigateTo('/privacy');
  const goTerms          = () => navigateTo('/terms');
  const goDisclaimer     = () => navigateTo('/disclaimer');
  const goTrackRecord     = () => navigateTo('/track-record');
  const goTradingResources = () => navigateTo('/trading-resources');

  // ── PUBLIC routes — no beta gate ────────────────────────────────
  if (route === 'brand-home') {
    return (
      <BrandHome
        onPerformanceLab={goPerformanceLab}
        onContact={goContact}
        onPrivacy={goPrivacy}
        onTerms={goTerms}
        onDisclaimer={goDisclaimer}
        onTradingResources={goTradingResources}
        lang={lang}
        setLang={saveLang}
      />
    );
  }
  if (route === 'trading-resources') {
    return <TradingResources onBack={goBackSmart} onContact={goContact} />;
  }
  if (route === 'contact') {
    return <Contact onBack={goBackSmart} onSignIn={goPerformanceLab} />;
  }
  if (route === 'legal-privacy' || route === 'legal-terms' || route === 'legal-disclaimer') {
    return <Legal page={route.replace('legal-', '')} onBack={goBackSmart} onSignIn={goPerformanceLab} />;
  }
  if (route === 'track-record') {
    return <TrackRecord onBack={goBackSmart} onSignIn={goPerformanceLab} />;
  }
  if (route?.startsWith('public-')) {
    return <PublicDashboard userId={route.replace('public-', '')} onBack={goBackSmart} onSignIn={goPerformanceLab} />;
  }
  if (route === 'reset-password') {
    return <ResetPassword onDone={async () => {
      try { await supabase.auth.signOut(); } catch (e) {}
      navigateTo('/performance-lab');
    }} />;
  }

  // ── PERFORMANCE LAB — gated ─────────────────────────────────────
  // 'confirming' (email-confirmation hash) also lands here so a fresh
  // sign-up goes through the gate then straight into the platform.
  if (route === 'performance-lab' || route === 'confirming') {
    return <PerformanceLabArea onBack={goBackSmart} />;
  }

  // Fallback — should not be reachable, but renders BrandHome if it is
  return (
    <BrandHome
      onPerformanceLab={goPerformanceLab}
      onContact={goContact}
      onPrivacy={goPrivacy}
      onTerms={goTerms}
      onDisclaimer={goDisclaimer}
      lang={lang}
      setLang={saveLang}
    />
  );
}

// The Performance Lab area: beta gate first, then the existing
// platform (AppInner) — completely unchanged platform behaviour.
function PerformanceLabArea({ onBack }) {
  const [unlocked, setUnlocked] = useState(() => isFounderUnlocked());
  if (!unlocked) {
    return <BetaGate onUnlock={() => setUnlocked(true)} onBack={onBack} />;
  }
  return <AppInner />;
}

export default function App() {
  // Lenis smooth scroll on desktop only. On touch devices (phones,
  // tablets) Lenis can get its scroll position out of sync when the
  // virtual keyboard opens for a focused input — the page stops
  // scrolling past that input. Native momentum scroll on mobile
  // looks just as good and avoids the bug entirely.
  //
  // We OR three signals so we catch every real touch device:
  //   - coarse pointer
  //   - no-hover capability
  //   - ontouchstart available (older iOS / some Android)
  const isTouch =
    typeof window !== 'undefined' &&
    (
      ('ontouchstart' in window) ||
      navigator.maxTouchPoints > 0 ||
      (window.matchMedia &&
        (window.matchMedia('(pointer: coarse)').matches ||
          window.matchMedia('(hover: none)').matches))
    );

  const inner = (
    <AuthProvider>
      <AmbientGradient />
      <RouteTransition>
        <BrandRouter />
      </RouteTransition>
    </AuthProvider>
  );

  if (isTouch) return inner;

  return (
    <ReactLenis
      root
      options={{
        lerp: 0.1,
        duration: 1.15,
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 1,
        touchMultiplier: 1.4,
      }}
    >
      {inner}
    </ReactLenis>
  );
}
